import AddIcon from "@mui/icons-material/Add";
import { Grid } from "@mui/material";
import ActivityTimeline from "@/components/ActivityTimeline";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  GROUP_THERAPIST_LABEL,
  THERAPIST_HISTORY_MAX_HEIGHT,
} from "../group-visit-constant";
import { UseGroupTherapists } from "../hooks/use-group-therapists";
import { useTherapistChangeHistory } from "../hooks/use-therapist-change-history";
import DetailCard from "./DetailCard";
import TherapistProfile from "./TherapistProfile";

export type ManageTherapistsTabProps = {
  therapists: UseGroupTherapists;
  onAddCoTherapist?: () => void;
  canEdit?: boolean;
};

/**
 * @author Rajkumar Rathod
 */
const ManageTherapistsTab = ({
  therapists,
  onAddCoTherapist,
  canEdit = true,
}: ManageTherapistsTabProps) => {
  const { primaryTherapist, coTherapists } = therapists;

  const { items: historyItems, isLoading: isHistoryLoading } =
    useTherapistChangeHistory();

  return (
    <Grid
      container
      spacing={2}
      sx={{
        alignItems: "flex-start",
      }}
    >
      <Grid
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Flex flexDirection="column" gap={2}>
          <DetailCard
            title={GROUP_THERAPIST_LABEL.primaryTherapist}
            titleVariant="field"
            required
          >
            {primaryTherapist ? (
              <TherapistProfile therapist={primaryTherapist} isPrimary />
            ) : (
              <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
                {GROUP_THERAPIST_LABEL.noPrimaryTherapist}
              </Text>
            )}
          </DetailCard>

          <DetailCard
            title={GROUP_THERAPIST_LABEL.coTherapists}
            titleVariant="field"
            action={
              canEdit && (
                <StyledButtonV2
                  label={GROUP_THERAPIST_LABEL.addCoTherapist}
                  variant="outlined"
                  onClick={onAddCoTherapist}
                  sIcon={<AddIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />}
                />
              )
            }
          >
            {coTherapists.length ? (
              <Flex flexDirection="column" gap={2}>
                {coTherapists.map((therapist) => (
                  <TherapistProfile
                    key={therapist.uuid}
                    therapist={therapist}
                  />
                ))}
              </Flex>
            ) : (
              <Flex justifyContent="center" py={3}>
                <Text
                  {...CHART_TYPOGRAPHY.body5}
                  c={CHART_COLORS.textMutedGrey}
                >
                  {GROUP_THERAPIST_LABEL.noCoTherapists}
                </Text>
              </Flex>
            )}
          </DetailCard>
        </Flex>
      </Grid>

      <Grid
        size={{
          xs: 12,
          lg: 4,
        }}
      >
        <DetailCard
          title={GROUP_THERAPIST_LABEL.changeHistory}
          action={
            <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
              {GROUP_THERAPIST_LABEL.auditLog}
            </Text>
          }
        >
          <ActivityTimeline
            items={historyItems}
            emptyText={
              isHistoryLoading
                ? GROUP_THERAPIST_LABEL.loadingHistory
                : GROUP_THERAPIST_LABEL.noHistory
            }
            sx={{
              maxHeight: THERAPIST_HISTORY_MAX_HEIGHT,
              overflowY: "auto",
              pr: 1,
            }}
          />
        </DetailCard>
      </Grid>
    </Grid>
  );
};

export default ManageTherapistsTab;
